import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, Globe } from "lucide-react";
import { api } from "@/lib/api";

const FALLBACK = {
    email: "hello@willmaxwellphoto.com",
    city: "Austin",
    region: "Texas",
    socials: [{ label: "Instagram", handle: "@willmaxwellphoto", url: "https://instagram.com/willmaxwellphoto" }],
};

function pickIcon(label) {
    const l = (label || "").toLowerCase().trim();
    if (l === "instagram") return Instagram;
    return Globe;
}

export default function Footer() {
    const [contact, setContact] = useState(FALLBACK);

    useEffect(() => {
        api.get("/site-content")
            .then((res) => {
                if (res.data?.contact) setContact({ ...FALLBACK, ...res.data.contact });
            })
            .catch(() => { /* fallback */ });
    }, []);

    return (
        <footer
            data-testid="site-footer"
            className="border-t border-[#E5DED5] bg-[#F2EFE9]/40 mt-24"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-3 gap-10">
                <div>
                    <h3 className="font-serif text-3xl tracking-tight">Will Maxwell</h3>
                    <p className="overline mt-1">Photography</p>
                    <p className="text-sm text-[#5C5853] mt-4 max-w-xs leading-relaxed">
                        Editorial portraits, large gatherings, and automotive stories — shot on film and digital{contact.city ? ` across ${contact.city}` : ""}.
                    </p>
                </div>
                <div>
                    <p className="overline mb-4">Navigate</p>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/" className="link-underline" data-testid="footer-link-home">Services & Pricing</Link></li>
                        <li><Link to="/portfolio" className="link-underline" data-testid="footer-link-portfolio">Portfolio</Link></li>
                        <li><Link to="/contact" className="link-underline" data-testid="footer-link-contact">Contact</Link></li>
                        <li><Link to="/admin/login" className="link-underline text-[#5C5853]" data-testid="footer-link-admin">Admin</Link></li>
                    </ul>
                </div>
                <div>
                    <p className="overline mb-4">Elsewhere</p>
                    <ul className="space-y-2 text-sm">
                        {contact.email && (
                            <li>
                                <a href={`mailto:${contact.email}`} data-testid="footer-link-email" className="link-underline inline-flex items-center gap-2 break-all">
                                    <Mail className="w-4 h-4 flex-shrink-0" /> {contact.email}
                                </a>
                            </li>
                        )}
                        {(contact.socials || []).slice(0, 4).map((s, i) => {
                            const Icon = pickIcon(s.label);
                            return (
                                <li key={`${s.label}-${i}`}>
                                    <a
                                        href={s.url || "#"}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`footer-link-${(s.label || "link").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className="link-underline inline-flex items-center gap-2"
                                    >
                                        <Icon className="w-4 h-4" /> {s.handle}
                                    </a>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </div>
            <div className="border-t border-[#E5DED5]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-[#5C5853]">
                    <span data-testid="footer-copyright">© {new Date().getFullYear()} Will Maxwell Photography. All rights reserved.</span>
                    {(contact.city || contact.region) && (
                        <span className="overline">{[contact.city, contact.region].filter(Boolean).join(" · ")}</span>
                    )}
                </div>
            </div>
        </footer>
    );
}
